import React, { JSX } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import TutorialsService from "@app/tutorials/tutorialsService";
import { TutorialContent, loadingData } from "@app/tutorials/tutorialsShared";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { breadcrumbTemplates, BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";

interface TutorialData {
  webLearningSeries: TutorialContent[];
  whatsNewWebinars: TutorialContent[];
}

export default class QESTutorials extends React.Component<never, TutorialData> {
  private readonly supportService = new TutorialsService();
  private readonly breadcrumbs = breadcrumbTemplates[BreadcrumbTemplateEnum.Tutorials]
  private readonly releaseNotes: TutorialContent[] = [{
    filename: "QES Release Notes",
    filetype: "txt",
    link: "https://questanalytics.gtmbuddy.io/shares/view/room/view/6880d01062bf7c149e2edf5c?pageId=68f7d2a07e6c813a5776c687",
    path: "",
    title: "QES Release Notes"
  }];

  constructor(props: never) {
    super(props);

    this.state = {
      webLearningSeries: loadingData,
      whatsNewWebinars: loadingData
    }
  }

  componentDidMount() {
    this.loadContent();
  }

  private loadContent = () => {
    Promise.all(
      [
        this.supportService.showBlobSas("whats-new-webinars"),
        this.supportService.showBlobSas("web-learning-series")
      ])
      .then(([whatsNewWebinarsStr, webLearningSeriesStr]) => {
        const whatsNewWebinars: TutorialContent[] = JSON.parse(whatsNewWebinarsStr);
        const webLearningSeries: TutorialContent[] = JSON.parse(webLearningSeriesStr);

        this.setState({ webLearningSeries, whatsNewWebinars });
      })
      .catch(() => {
        alert("Could not load content, please reload the page in a few moments.");
      });
  }

  private rowPopulator(links: TutorialContent[]) {
    const buildRows = links.map((row, index) => this.renderLines(row, index));
    return (
      <div className="grid-striped">
        {buildRows}
      </div>
    );
  }

  private renderLines(row: TutorialContent, index: number) {
    let linkImageElement: JSX.Element = null;
    let titleStyle = "";
    switch (row.filetype) {
      case "pdf":
        linkImageElement = (
          <QesIcon iconName={QesIcons.ShareV4} description="Open" className="text-info mb3"/>
        );
        break;
      case "mp4":
        linkImageElement = (
          <QesIcon iconName={QesIcons.Play} description="Open" className="mb3" />
        );
        titleStyle = "text-info fst-italic";
        break;
      case "txt":
        linkImageElement = (
          <QesIcon iconName={QesIcons.ShareV4} description="Open" className="text-info mb3" />
        );
        titleStyle = "text-info fst-italic";
        break;
      default:
        linkImageElement = (
          <QesIcon iconName={QesIcons.Play} description="Open" className="mb3" />
        );
    }

    return (
      <div className={"d-flex flex-row align-items-center w-100 pb10 pt10 grid-row " + titleStyle} key={index}>

        <p className="ml10 mb0 mr10">
          <a className="text-decoration-none" style={{ color: "inherit" }} href={row.link} target="_blank" rel="noreferrer">
            {linkImageElement} <span className="mb0 mt0 ml10">{row.title}</span>
          </a>
        </p>
      </div>
    );
  }

  public render() {
    const webLearningSeriesList = this.rowPopulator(this.state.webLearningSeries);
    const whatsNewWebinarsList = this.rowPopulator(this.state.whatsNewWebinars);
    const releaseNotesList = this.rowPopulator(this.releaseNotes);

    return (
      <Page documentTitle="QES Tutorials">
        <Breadcrumbs
          items={this.breadcrumbs}
        />
        <PageInner>
          <PageHeader
            title="QES Tutorials"
            iconName={QesIcons.PencilV1}
          />
          <div className="row">
            <div className="col-12">
              <div className="ms-auto">
                <a className="float-end text-decoration-none" href="mailto:clientservices@questanalytics.com">
                  <QesIcon iconName={QesIcons.MailV1} description="Email" className="mb2 me-1"/>clientservices@questanalytics.com
                </a>
              </div>
            </div>
          </div>

          <div className="row mt30">
            <div className="col-12">
              <hr className="mt0"/>
            </div>
          </div>

          <PageSection>
            <div className="row card-striped">
              <div className="col-12 col-sm-8">
                <AccordionContainer>
                  <Accordion title="QES What's New Webinars" testId="tutorials__whats-new-webinar-accordion">
                    {whatsNewWebinarsList}
                  </Accordion>
                </AccordionContainer>
              </div>
            </div>
          </PageSection>

          <div className="row">
            <div className="col-12">
              <hr className="mt0"/>
            </div>
          </div>

          <PageSection>
            <div className="row card-striped">
              <div className="col-12 col-sm-8">
                <AccordionContainer>
                  <Accordion title="QES Web Learning Series" testId="tutorials__web-learning-series-accordion">
                    {webLearningSeriesList}
                  </Accordion>
                </AccordionContainer>
              </div>
            </div>
          </PageSection>

          <div className="row">
            <div className="col-12">
              <hr className="mt0"/>
            </div>
          </div>

          <PageSection>
            <div className="row card-striped">
              <div className="col-12 col-sm-8">
                <AccordionContainer>
                  <Accordion title="QES Release Notes" testId="tutorials__release-notes-accordion">
                    {releaseNotesList}
                  </Accordion>
                </AccordionContainer>
              </div>
            </div>
          </PageSection>
        </PageInner>
      </Page>
    );
  }
}

export { QESTutorials };
